/**
 * Panic Coach Tool
 *
 * Guided breathing protocol for panic attacks.
 * Flow: intro (stress 1-5) → setup (duration + pattern) → session → complete.
 */

import {
    StepFlow, CountdownTimer, BreathingAnimation, BreathingSounds,
    formatTime, saveToolEntry, initButtonGroup, renderFearChart
} from './tools-common';

var PATTERN_MAP = {
    '2:3': { inhale: 2, hold: 0, exhale: 3 },
    '5:5': { inhale: 5, hold: 0, exhale: 5 },
    '4:6': { inhale: 4, hold: 2, exhale: 6 }
};

export function initPanicCoach() {
    var root = document.getElementById('panic-coach');
    if (!root) return;

    var flow = new StepFlow(root);
    var timer = null;
    var breathing = null;
    var sounds = new BreathingSounds();
    var selectedDuration = 240;
    var selectedPattern = '4:6';
    var initialStress = 3;
    var fearRatings = [];
    var fearTimestamps = [];
    var fearCheckInterval = null;
    var sessionStartTime = null;
    var currentCycle = 0;
    var totalCycles = 6;
    var paused = false;

    // Sound toggle
    var soundBtn = document.getElementById('panic-sound-toggle');
    var iconOff = document.getElementById('panic-sound-icon-off');
    var iconOn = document.getElementById('panic-sound-icon-on');
    if (soundBtn) {
        soundBtn.addEventListener('click', function () {
            var on = sounds.toggle();
            soundBtn.classList.toggle('active', on);
            if (iconOff) iconOff.style.display = on ? 'none' : 'block';
            if (iconOn) iconOn.style.display = on ? 'block' : 'none';
        });
    }

    // —— Intro: stress 1-5 + Continue ——
    initButtonGroup(root, '.tool-stress-btn', function (btn) {
        initialStress = parseInt(btn.dataset.stress, 10);
    });
    var introContinueBtn = root.querySelector('.tool-intro-continue-btn');
    if (introContinueBtn) {
        introContinueBtn.addEventListener('click', function () {
            flow.show('setup');
        });
    }

    // —— Setup: duration + pattern + Play ——
    initButtonGroup(root, '.tool-duration-btn', function (btn) {
        selectedDuration = parseInt(btn.dataset.duration, 10);
    });
    initButtonGroup(root, '.tool-pattern-btn', function (btn) {
        selectedPattern = btn.dataset.pattern || '4:6';
    });

    var phases = [
        { at: 1.0, text: 'This is a panic wave, not danger. It will pass.' },
        { at: 0.85, text: 'Follow the breathing circle. In for 4, hold for 2, out for 6.' },
        { at: 0.6, text: 'Where do you feel it? Chest, hands, stomach? Just notice.' },
        { at: 0.35, text: 'The wave is cresting. You are doing this.' },
        { at: 0.15, text: 'The intensity is fading. You rode the wave.' },
    ];

    function updatePhase(remaining, total) {
        var ratio = remaining / total;
        var current = phases[0];
        for (var i = 0; i < phases.length; i++) {
            if (ratio <= phases[i].at) current = phases[i];
        }
        var textEl = document.getElementById('panic-phase-text');
        if (textEl) textEl.textContent = current.text;
    }

    function updateSessionBar() {
        var cycleEl = document.getElementById('panic-cycle-label');
        if (cycleEl) cycleEl.textContent = 'Cycle ' + Math.min(currentCycle, totalCycles) + ' / ' + totalCycles;
        var stressEl = document.getElementById('panic-stress-pill');
        if (stressEl) {
            var pct = 0;
            if (fearRatings.length > 0) {
                var sum = fearRatings.reduce(function (a, b) { return a + b; }, 0);
                pct = Math.round((sum / fearRatings.length) / 10 * 100);
            } else {
                pct = Math.round((initialStress / 5) * 100);
            }
            stressEl.textContent = 'Stress ' + pct + '%';
        }
    }

    // Start session (Play)
    root.querySelector('.tool-start-btn').addEventListener('click', function () {
        flow.show('session');
        fearRatings = [];
        fearTimestamps = [];
        sessionStartTime = Date.now();
        currentCycle = 0;
        paused = false;
        var pauseBtn = document.getElementById('panic-pause-btn');
        if (pauseBtn) {
            pauseBtn.classList.remove('tool-pause-btn--paused');
            var iconPause = pauseBtn.querySelector('.tool-pause-btn__icon--pause');
            var iconPlay = pauseBtn.querySelector('.tool-pause-btn__icon--play');
            if (iconPause) iconPause.style.display = '';
            if (iconPlay) iconPlay.style.display = 'none';
        }

        updateSessionBar();

        var pattern = PATTERN_MAP[selectedPattern] || PATTERN_MAP['4:6'];
        var cycleSeconds = pattern.inhale + pattern.hold + pattern.exhale;
        totalCycles = Math.max(1, Math.ceil(selectedDuration / cycleSeconds));

        timer = new CountdownTimer(selectedDuration, function (remaining, total) {
            updatePhase(remaining, total);
            updateSessionBar();
        }, function () {
            endSession(true);
        });
        timer.start();

        breathing = new BreathingAnimation(
            document.getElementById('panic-breathing-circle'),
            document.getElementById('panic-breath-instruction'),
            document.getElementById('panic-breath-counter'),
            sounds,
            pattern
        );
        breathing.onCycleComplete = function () {
            currentCycle++;
            updateSessionBar();
        };
        breathing.start();

        var checkEvery = selectedDuration <= 60 ? 10000 : 30000;
        fearCheckInterval = setInterval(function () {
            if (timer && timer.remaining > 10 && !paused) showFearRating();
        }, checkEvery);
    });

    function showFearRating() {
        var el = document.getElementById('panic-fear-rating');
        if (el) el.classList.remove('hidden');
    }

    var fearSubmitBtn = document.getElementById('panic-fear-submit');
    if (fearSubmitBtn) {
        fearSubmitBtn.addEventListener('click', function () {
            var slider = document.getElementById('panic-fear-slider');
            var elapsed = Math.round((Date.now() - sessionStartTime) / 1000);
            fearRatings.push(parseInt(slider.value, 10));
            fearTimestamps.push(elapsed);
            document.getElementById('panic-fear-rating').classList.add('hidden');
            slider.value = 5;
            updateSessionBar();
        });
    }

    // Pause / Resume
    var pauseBtn = document.getElementById('panic-pause-btn');
    if (pauseBtn) {
        pauseBtn.addEventListener('click', function () {
            var iconPause = pauseBtn.querySelector('.tool-pause-btn__icon--pause');
            var iconPlay = pauseBtn.querySelector('.tool-pause-btn__icon--play');
            if (paused) {
                timer.resume();
                if (breathing) breathing.start();
                paused = false;
                pauseBtn.classList.remove('tool-pause-btn--paused');
                if (iconPause) iconPause.style.display = '';
                if (iconPlay) iconPlay.style.display = 'none';
            } else {
                timer.pause();
                if (breathing) breathing.stop();
                paused = true;
                pauseBtn.classList.add('tool-pause-btn--paused');
                if (iconPause) iconPause.style.display = 'none';
                if (iconPlay) iconPlay.style.display = '';
            }
        });
    }

    function endSession(completed) {
        if (timer) timer.stop();
        if (breathing) breathing.stop();
        if (fearCheckInterval) clearInterval(fearCheckInterval);
        sounds.disable();

        var chartEl = document.getElementById('panic-chart');
        renderFearChart(chartEl, fearRatings, fearTimestamps, selectedDuration);

        // Stats: DURATION, AVG STRESS, Start/End
        var durationEl = document.getElementById('panic-stat-duration');
        if (durationEl) durationEl.textContent = formatTime(selectedDuration);

        var avgEl = document.getElementById('panic-stat-avg');
        if (avgEl) {
            if (fearRatings.length > 0) {
                var sum = fearRatings.reduce(function (a, b) { return a + b; }, 0);
                avgEl.textContent = (sum / fearRatings.length).toFixed(1);
            } else {
                avgEl.textContent = '—';
            }
        }

        var startEndEl = document.getElementById('panic-stat-start-end');
        if (startEndEl) {
            if (fearRatings.length >= 2) {
                startEndEl.textContent = fearRatings[0] + ' → ' + fearRatings[fearRatings.length - 1];
            } else if (fearRatings.length === 1) {
                startEndEl.textContent = String(fearRatings[0]);
            } else {
                startEndEl.textContent = '—';
            }
        }

        saveToolEntry({
            tool_slug: 'panic-coach',
            entry_type: 'session',
            data: {
                duration: selectedDuration,
                completed: completed,
                initial_stress: initialStress,
                breathing_pattern: selectedPattern,
                fear_ratings: fearRatings,
                fear_timestamps: fearTimestamps,
                avg_stress: fearRatings.length ? (fearRatings.reduce(function (a, b) { return a + b; }, 0) / fearRatings.length) : null,
                start_end: fearRatings.length >= 2 ? [fearRatings[0], fearRatings[fearRatings.length - 1]] : null,
            },
        });

        flow.show('complete');
    }

    root.querySelectorAll('.tool-stop-btn').forEach(function (btn) {
        btn.addEventListener('click', function () { endSession(false); });
    });

    root.querySelectorAll('.tool-restart-btn').forEach(function (btn) {
        btn.addEventListener('click', function () {
            sounds.disable();
            if (soundBtn) soundBtn.classList.remove('active');
            if (iconOff) iconOff.style.display = 'block';
            if (iconOn) iconOn.style.display = 'none';
            flow.reset();
        });
    });
}
